import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  InputSignal,
  signal,
  Signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { LoadingAnimationComponent } from '../../../components/feedback/loading-animation/loading-animation.component';
import { TicketTileComponent } from '../../../components/data-display/ticket-tile/ticket-tile.component';
import { ToTicketTilePipe } from '../../../pipes/to-ticket-tile/to-ticket-tile.pipe';
import { ActiveDirectTicketStore } from '../../../services/api/direct-ticket/active/active-direct-ticket-store.service';
import { ConfigurationStore } from '../../../services/api/configuration/configuration-store.service';
import {
  BREAKPOINT_MD,
  CONFIGURATION_SETTING_KEYS,
  EEntityType,
  ERelationType,
  ICorporateDesign,
  IDirectTicket,
  ITicketCategory,
  TicketCategoryId,
} from '@web/dtos';
import { Router } from '@angular/router';
import { BannerComponent } from '../../../components/data-display/banner/banner.component';
import { OrderDirectTicketSupportService } from '../../../services/support/order-ticket-support/order-direct-ticket-support.service';
import { CorporateDesignStore } from '../../../services/api/corporate-design/corporate-design-store.service';
import { NgStyle, NgTemplateOutlet } from '@angular/common';
import { CorporateDesignService } from '../../../services/corporate-design/corporate-design.service';
import { EqualizingService } from '../../../services/support/equalizing-support/equalizing.service';
import {
  DirectTicketConfigurationShoppingCartComponent,
  EPage,
} from '../../../components/feedback/direct-ticket-configuration-shopping-cart/direct-ticket-configuration-shopping-cart.component';
import { RelationSearchComponent } from '../../../components/data-input/relation-search/relation-search.component';
import { AppendTestIdPipe } from '../../../pipes/append-test-id/append-test-id.pipe';
import { FilterTextsPipe } from '../../../pipes/filterTexts/filterTexts.pipe';
import { WebTextStore } from '../../../services/api/text/web-text-store.service';
import { LanguagePipe } from '../../../pipes/language/language.pipe';
import { JourneyPlannerComponent } from '../../../components/data-input/journey-planner/journey-planner.component';
import { TicketCategoryStore } from '../../../services/api/ticket-category/ticket-category-store.service';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ToggleChipComponent } from '../../../components/data-display/toggle-chip/toggle-chip.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { startWith } from 'rxjs';
import { EConfigurationChannel } from '@web/dtos';

export interface ITicketCategorySelectionForm {
  id: FormControl<TicketCategoryId>;
  name: FormControl<string>;
  position: FormControl<number>;
  isActive: FormControl<boolean>;
}

@Component({
  selector: 'aw-direct-tickets-page',
  imports: [
    LoadingAnimationComponent,
    TicketTileComponent,
    ToTicketTilePipe,
    BannerComponent,
    NgStyle,
    DirectTicketConfigurationShoppingCartComponent,
    RelationSearchComponent,
    NgTemplateOutlet,
    AppendTestIdPipe,
    FilterTextsPipe,
    LanguagePipe,
    JourneyPlannerComponent,
    JourneyPlannerComponent,
    ReactiveFormsModule,
    ToggleChipComponent,
  ],
  templateUrl: './direct-tickets-page.component.html',
})
export class DirectTicketsPageComponent implements AfterViewChecked {
  public readonly ctx: InputSignal<string | undefined> = input();

  private readonly directTicketStore: ActiveDirectTicketStore = inject(
    ActiveDirectTicketStore
  );
  protected readonly configurationStore: ConfigurationStore =
    inject(ConfigurationStore);
  private readonly corporateDesignStore: CorporateDesignStore =
    inject(CorporateDesignStore);
  private readonly router: Router = inject(Router);
  private readonly cdRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  public readonly orderTicketSupport: OrderDirectTicketSupportService = inject(
    OrderDirectTicketSupportService
  );
  public readonly corporateDesignService: CorporateDesignService = inject(
    CorporateDesignService
  );
  public readonly textStore: WebTextStore = inject(WebTextStore);
  private readonly equalizingService: EqualizingService =
    inject(EqualizingService);
  public readonly ticketCategoryStore: TicketCategoryStore =
    inject(TicketCategoryStore);

  public readonly corporateDesign: Signal<ICorporateDesign> =
    this.corporateDesignStore.corporateDesign;
  public readonly isLoadingFixedPriceTickets: Signal<boolean> = computed(
    () =>
      this.directTicketStore.isLoadingFixedPriceTickets() ||
      this.corporateDesignStore.isLoading()
  );
  public readonly isLoadingRelationTickets: Signal<boolean> = computed(
    () =>
      this.directTicketStore.isLoadingRelationTickets() ||
      this.corporateDesignStore.isLoading()
  );
  public readonly fixedPriceTickets: Signal<IDirectTicket[]> = computed(() => {
    return this.directTicketStore.fixedPriceTickets();
  });
  public readonly visibleFixedPriceTickets: Signal<IDirectTicket[]> = computed(
    () => {
      const selectedCategoryId: TicketCategoryId | null =
        this.selectedFixedPriceTicketCategoryId();
      const tickets: IDirectTicket[] = this.fixedPriceTickets();
      const sortedTickets: IDirectTicket[] = tickets.sort(
        (a: IDirectTicket, b: IDirectTicket) => {
          return (
            (a.ticketData?.positionInCategory || 0) -
            (b.ticketData?.positionInCategory || 0)
          );
        }
      );
      if (!selectedCategoryId) {
        return [];
      }

      return sortedTickets.filter(
        (ticket: IDirectTicket) =>
          ticket.ticketData?.ticketCategoryId === selectedCategoryId
      );
    }
  );
  public readonly relationTickets: Signal<IDirectTicket[]> = computed(() => {
    const categoryId = this.selectedRelationTicketCategoryId();
    return this.directTicketStore
      .relationTickets()
      .filter(ticket => ticket.ticketData?.ticketCategoryId === categoryId);
  });

  protected readonly minHeight: WritableSignal<number> = signal(0);
  protected readonly windowWidth: WritableSignal<number> = signal(
    window.innerWidth
  );
  protected readonly isMobile: Signal<boolean> = computed(() => {
    const mdBreakpoint: number = BREAKPOINT_MD;
    return this.windowWidth() < mdBreakpoint;
  });
  public selectedDirectTicketCategoryIdControl: FormControl<TicketCategoryId | null> =
    new FormControl<TicketCategoryId | null>(null);
  public selectedRelationTicketCategoryIdControl: FormControl<TicketCategoryId | null> =
    new FormControl<TicketCategoryId | null>(null);

  public readonly selectedFixedPriceTicketCategoryId: Signal<TicketCategoryId | null> =
    toSignal(
      this.selectedDirectTicketCategoryIdControl.valueChanges.pipe(
        startWith(this.selectedDirectTicketCategoryIdControl.value)
      ),
      { initialValue: this.selectedDirectTicketCategoryIdControl.value }
    );
  public readonly selectedRelationTicketCategoryId: Signal<TicketCategoryId | null> =
    toSignal(
      this.selectedRelationTicketCategoryIdControl.valueChanges.pipe(
        startWith(this.selectedRelationTicketCategoryIdControl.value)
      ),
      { initialValue: this.selectedRelationTicketCategoryIdControl.value }
    );

  @ViewChild('shoppingCart') shoppingCart: ElementRef<HTMLElement> | undefined;

  @HostListener('window:resize', ['$event'])
  windowResize() {
    this.windowWidth.set(window.innerWidth);
    this.equalize();
  }
  protected readonly EEntityType: typeof EEntityType = EEntityType;
  protected readonly ERelationType: typeof ERelationType = ERelationType;
  protected readonly EPage: typeof EPage = EPage;

  constructor() {
    effect(() => {
      this.ticketCategoryStore.writableTicketCategories.set(
        this.ticketCategoryStore.ticketCategories()
      );
      this.initializeSelectedCategory();
    });
    effect((): void => {
      if (!this.configurationStore.isLoading()) {
        if (
          !this.configurationStore.getBooleanConfiguration(
            CONFIGURATION_SETTING_KEYS.IsDirectSaleActive
          )
        ) {
          this.router.navigate(['/']);
        }
      }
    });
    effect(() => {
      if (
        this.fixedPriceTickets() &&
        !this.isLoadingFixedPriceTickets() &&
        !this.isLoadingRelationTickets()
      ) {
        this.equalize();
      }
    });
  }

  ngAfterViewChecked(): void {
    if (this.shoppingCart) {
      this.minHeight.set(
        this.shoppingCart.nativeElement.getBoundingClientRect().height
      );
    }
  }

  private initializeSelectedCategory(): void {
    const firstDirectCategory: ITicketCategory =
      this.ticketCategoryStore.sortedDirectTicketCategories()[0];
    const firstRelationCategory: ITicketCategory =
      this.ticketCategoryStore.sortedRelationTicketCategories()[0];

    if (!firstDirectCategory?.id || !firstRelationCategory?.id) {
      return;
    }

    this.selectedDirectTicketCategoryIdControl.setValue(firstDirectCategory.id);
    this.selectedRelationTicketCategoryIdControl.setValue(
      firstRelationCategory.id
    );
  }

  public selectTicketCategory(ticketCategoryId: TicketCategoryId): void {
    if (
      this.selectedDirectTicketCategoryIdControl.value === ticketCategoryId ||
      this.selectedRelationTicketCategoryIdControl.value === ticketCategoryId
    ) {
      return;
    }

    const relationCategory: ITicketCategory[] = this.ticketCategoryStore
      .sortedRelationTicketCategories()
      .filter(category => category.id === ticketCategoryId);
    const directCategory: ITicketCategory[] = this.ticketCategoryStore
      .sortedDirectTicketCategories()
      .filter(category => category.id === ticketCategoryId);

    if (directCategory.length > 0) {
      this.selectedDirectTicketCategoryIdControl.setValue(ticketCategoryId);
    }
    if (relationCategory.length > 0) {
      this.selectedRelationTicketCategoryIdControl.setValue(ticketCategoryId);
    }
  }

  protected isCategoryChipSelected(categoryId: TicketCategoryId): boolean {
    return (
      this.selectedDirectTicketCategoryIdControl.value === categoryId ||
      this.selectedRelationTicketCategoryIdControl.value === categoryId
    );
  }

  protected equalize(): void {
    this.cdRef.detectChanges();
    const toEqual: string[] = [
      'upper-ticket-segment-ticket-tile',
      'lower-ticket-segment-ticket-tile',
    ];
    this.equalizingService.equalize(toEqual);
  }

  protected readonly CONFIGURATION_SETTING_KEYS = CONFIGURATION_SETTING_KEYS;
  protected readonly EConfigurationChannel = EConfigurationChannel;
}
